import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  MultiSelect,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { IconX } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import type { IVocabTermStats } from "../../../../../lib/api"
import { formatInteger, formatRelative, parsePgTimestamp } from "../../../../../lib/format-date"
import { findNearDuplicates } from "../../company-words"
import { SettingsReadOnlyNotice } from "../../SettingsReadOnlyNotice"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** A term that was never heard after this many counted meetings is marked quiet. */
const QUIET_AFTER_MEETINGS = 20

function isQuietChip(term: IVocabTermStats, meetingsCounted: number): boolean {
  if (meetingsCounted < QUIET_AFTER_MEETINGS) return false
  return term.lastSeenAt === null || term.hits === 0
}

function UsageTooltip({ term, now }: { term: IVocabTermStats; now: number }): React.JSX.Element {
  const { i18n } = useLingui()
  const then = parsePgTimestamp(term.lastSeenAt)
  const relative = then ? formatRelative(i18n, now - then.getTime(), { dayGranular: true }) : null
  return (
    <span>
      <Plural value={term.hits} one="Heard # time" other="Heard # times" />{" "}
      <Plural value={term.meetings} one="in # meeting" other="in # meetings" />
      {" · "}
      {relative ? <Trans>last heard {relative}</Trans> : <Trans>never heard</Trans>}
      {" · "}
      <Plural value={term.corrections} one="fixed # mishearing" other="fixed # mishearings" />
    </span>
  )
}

export function TranscriptionPanelView({
  settings,
  saving,
  isAdminOnly,
  canManage = true,
  company,
  vocabTerms,
  addingVocab,
  vocabInput,
  updateSetting,
  save,
  onVocabInputChange,
  onAddVocab,
  onRemoveVocab,
  vocabStats,
  meetingsCounted = 0,
}: IProps): React.JSX.Element {
  const [now] = useState<number>(() => Date.now())
  const { i18n } = useLingui()

  const statsByTerm = useMemo(() => new Map((vocabStats ?? []).map((s) => [s.term, s])), [vocabStats])

  const rankedTerms = useMemo(() => {
    if (!vocabStats || vocabStats.length === 0) return vocabTerms
    return [...vocabTerms].sort(
      (a, b) =>
        (statsByTerm.get(a)?.rank ?? Number.POSITIVE_INFINITY) - (statsByTerm.get(b)?.rank ?? Number.POSITIVE_INFINITY),
    )
  }, [vocabTerms, vocabStats, statsByTerm])

  const nearDuplicates = useMemo(() => findNearDuplicates(rankedTerms), [rankedTerms])

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Text fw={600} mb="xs">
          <Trans>Default language</Trans>
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          <Trans>Select which languages to detect. Leave empty to auto-detect all languages.</Trans>
        </Text>
        <MultiSelect
          data={[
            { value: "nl", label: i18n._(msg`Dutch`) },
            { value: "en", label: i18n._(msg`English`) },
            { value: "de", label: i18n._(msg`German`) },
            { value: "fr", label: i18n._(msg`French`) },
            { value: "es", label: i18n._(msg`Spanish`) },
            { value: "it", label: i18n._(msg`Italian`) },
            { value: "pt", label: i18n._(msg`Portuguese`) },
          ]}
          value={
            settings.default_language && settings.default_language !== "multi"
              ? settings.default_language.split(",").filter(Boolean)
              : []
          }
          onChange={(vals) => updateSetting("default_language", vals.length > 0 ? vals.join(",") : "multi")}
          placeholder={i18n._(msg`Auto-detect all languages`)}
          clearable
          disabled={!canManage}
        />
      </Paper>
      <Paper withBorder p="md">
        <Group justify="space-between" wrap="nowrap" gap="md">
          <div>
            <Text fw={600}>
              <Trans>Clean up transcripts</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>
                Before generating the summary, run an AI pass that removes filler words ("uh", "um"), false starts and
                stutters, and lightly fixes grammar. The original transcript is backed up and can be restored from each
                meeting's transcript page.
              </Trans>
            </Text>
          </div>
          <Switch
            checked={settings.transcript_cleanup_enabled === "true"}
            disabled={!canManage}
            onChange={(e) => updateSetting("transcript_cleanup_enabled", e.currentTarget.checked ? "true" : "false")}
            aria-label={i18n._(msg`Clean up transcripts before analysis`)}
          />
        </Group>
      </Paper>
      {canManage ? (
        <Button onClick={() => save(["default_language", "transcript_cleanup_enabled"])} loading={saving}>
          <Trans>Save</Trans>
        </Button>
      ) : (
        <SettingsReadOnlyNotice />
      )}

      {(!!company || isAdminOnly) && (
        <>
          <Title order={4} mt="sm">
            <Trans>Company words</Trans>
          </Title>
          <Alert variant="light" color="info">
            <Text size="sm">
              <Trans>
                Add names, products and abbreviations people say in your meetings. We send them to the transcription
                service so they come out spelled right.
              </Trans>
              <br />
              <Text span c="dimmed" size="xs">
                <Trans>
                  Tip: you can also correct words in a transcript — the system will suggest adding them automatically.
                </Trans>
              </Text>
            </Text>
          </Alert>
          <Paper withBorder p="md">
            <Stack gap="sm">
              <Group gap="xs" wrap="wrap">
                {rankedTerms.map((term) => {
                  const stat = statsByTerm.get(term)
                  const quiet = stat ? isQuietChip(stat, meetingsCounted) : false
                  const similar = nearDuplicates.get(term)
                  const chipColor = similar ? "warning" : "info"
                  const tooltip = similar ? (
                    <Trans>Looks like {similar}</Trans>
                  ) : quiet ? (
                    <Trans>Not heard in the last 20 meetings</Trans>
                  ) : stat ? (
                    <UsageTooltip term={stat} now={now} />
                  ) : (
                    i18n._(msg`Remove`)
                  )
                  return (
                    <Tooltip key={term} label={tooltip} withArrow>
                      <Badge
                        variant="light"
                        color={chipColor}
                        size="md"
                        tt="none"
                        className={classes.vocabChip}
                        classNames={{ label: classes.vocabLabel }}
                        data-quiet={quiet || undefined}
                        data-near-duplicate={similar || undefined}
                        rightSection={
                          <ActionIcon
                            size="xs"
                            color={chipColor}
                            variant="transparent"
                            onClick={() => onRemoveVocab(term)}
                            aria-label={i18n._(msg`Remove ${term}`)}
                          >
                            <IconX size={12} />
                          </ActionIcon>
                        }
                      >
                        {term}
                        {stat && stat.hits > 0 ? (
                          <span className={classes.hitCount}>×{formatInteger(i18n, stat.hits)}</span>
                        ) : null}
                      </Badge>
                    </Tooltip>
                  )
                })}
                {vocabTerms.length === 0 && (
                  <Text size="sm" c="dimmed">
                    <Trans>No custom terms yet.</Trans>
                  </Text>
                )}
              </Group>
              <Group gap="xs" align="flex-end">
                <TextInput
                  placeholder={i18n._(msg`e.g. Lexicanon, BYOK, KPI — comma-separated`)}
                  value={vocabInput}
                  onChange={(e) => onVocabInputChange(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onAddVocab()
                  }}
                  style={{ flex: 1 }}
                />
                <Button onClick={onAddVocab} loading={addingVocab} disabled={!vocabInput.trim()}>
                  <Trans>Add</Trans>
                </Button>
              </Group>
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  )
}
